import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getCurrencyBySubUnits } from '../../../../shared';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { localStorageService } from '../../../../shared/services/local-storage';
import { useLoyaltyStore } from '../../hooks';
import {
    getLoyaltyIconByMachineType,
    getLoyaltyNounByMachineType,
    LOYALTY_BANNER_SEEN_DELAY_MS,
} from '../../loyalty.constants';

import styles from './LoyaltyBanner.module.css';

/**
 * One-time informational banner announcing the loyalty program on the current
 * machine (SWRN-1747). Shown once per browser — counted only after it has
 * actually been on screen for `LOYALTY_BANNER_SEEN_DELAY_MS` — while the
 * loyalty program is active, and it hides as soon as the payment buttons
 * appear. The reward step comes from `pointsToShowLoyaltyBonus`, the same field
 * the balance badge and the progress track use.
 */
export const LoyaltyBanner = (): JSX.Element | null => {
    const { t } = useTranslation();

    const vm = useAppStore((state) => state.vm);
    const isLoyaltyProgramActive = useLoyaltyStore((state) =>
        state.isLoyaltyProgramActive(),
    );
    const arePaymentButtonsVisible = useLoyaltyStore(
        (state) => state.arePaymentButtonsVisible,
    );
    const rewardStep = useLoyaltyStore((state) => state.pointsToShowLoyaltyBonus);
    const minPriceSubunits = useLoyaltyStore(
        (state) => state.productMinPriceToGetBonusPoints,
    );

    const [isDismissed, setIsDismissed] = useState<boolean>(() =>
        Boolean(localStorageService.getItem('LOYALTY_BANNER_SEEN')),
    );

    const isVisible =
        isLoyaltyProgramActive && !isDismissed && !arePaymentButtonsVisible;

    // An explicit dismiss is proof the user saw it — persist immediately instead
    // of waiting out LOYALTY_BANNER_SEEN_DELAY_MS.
    const handleClose = (): void => {
        localStorageService.setItem('LOYALTY_BANNER_SEEN', '1');
        setIsDismissed(true);
    };

    // Effects run after the render is committed, so the banner is genuinely on
    // screen here. The cleanup cancels the timer when it hides or unmounts, so a
    // single-frame render can no longer burn the one-time flag (SWRN-1747).
    useEffect(() => {
        if (!isVisible) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            localStorageService.setItem('LOYALTY_BANNER_SEEN', '1');
        }, LOYALTY_BANNER_SEEN_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [isVisible]);

    if (!isVisible) {
        return null;
    }

    const MachineIcon = getLoyaltyIconByMachineType(vm.type_id);
    const noun = getLoyaltyNounByMachineType(vm.type_id);
    const currencyLabel = t(`Currency.${vm.currency_code}`);
    const minPrice = `${getCurrencyBySubUnits(minPriceSubunits).toFixed(2)} ${currencyLabel}`;

    return (
        <div className={styles.banner} role="status">
            <span className={styles.iconWrap}>
                <MachineIcon size="medium" />
            </span>
            <div className={styles.text}>
                <p className={styles.title}>{t('loyalty.banner.title')}</p>
                {rewardStep > 0 && (
                    <p className={styles.body}>
                        {t(`loyalty.banner.body.${noun}`, { n: rewardStep })}
                    </p>
                )}
                {minPriceSubunits > 0 && (
                    <p className={styles.minPrice}>
                        {t('loyalty.banner.minPrice', { price: minPrice })}
                    </p>
                )}
            </div>
            <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label={t<string>('loyalty.banner.close')}
            >
                &#10005;
            </button>
        </div>
    );
};
